import React, { useState, useEffect } from 'react';
import { Bell, User, Search, Settings, LogOut, Menu } from 'lucide-react';

const WMSHeader = ({ currentUser, onLogout, onMenuToggle }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  useEffect(() => {
    const controlHeader = () => {
      // Only show header when at the very top of the page
      if (window.scrollY <= 10) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
      setLastScrollY(window.scrollY);
    };

    window.addEventListener('scroll', controlHeader);
    return () => window.removeEventListener('scroll', controlHeader);
  }, [lastScrollY]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close all dropdowns when clicking outside
      const dropdownContainers = document.querySelectorAll('[data-dropdown]');
      let isInsideDropdown = false;
      
      dropdownContainers.forEach(container => {
        if (container.contains(event.target)) {
          isInsideDropdown = true;
        }
      });
      
      if (!isInsideDropdown) {
        setShowNotifications(false);
        setShowSettings(false);
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close all dropdowns helper
  const closeAllDropdowns = () => {
    setShowNotifications(false);
    setShowSettings(false);
    setShowUserMenu(false);
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: isVisible ? '0px' : '-120px',
        left: '0px',
        right: '0px',
        zIndex: 1000,
        backgroundColor: 'white',
        borderBottom: '2px solid #2563eb',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        height: '100px',
        transition: 'top 0.3s ease-in-out',
        minHeight: '100px',
        maxHeight: '100px'
      }}
    >
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        padding: '0 16px', 
        height: '100%' 
      }}>
        {/* Mobile Menu Button & Logo */}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {/* Mobile Menu Button */}
          <button
            onClick={onMenuToggle}
            style={{
              display: 'block',
              padding: '8px',
              color: '#374151',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              marginRight: '8px'
            }}
            className="lg:hidden"
          >
            <Menu style={{ width: '24px', height: '24px' }} />
          </button>

          {/* Logo & Company Name */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{
              width: '48px',
              height: '48px',
              background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '16px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}>
              <div style={{ color: 'white', fontWeight: 'bold', fontSize: '20px' }}>+</div>
            </div>
            <div>
              <div style={{ 
                fontSize: '16px', 
                fontWeight: 'bold', 
                color: '#1f2937',
                lineHeight: '1.3',
                marginBottom: '2px'
              }}>
                UNIQUE MEDICAL SUPPLY LTD.
              </div>
              <div style={{ 
                fontSize: '11px', 
                color: '#2563eb', 
                fontWeight: '500',
                lineHeight: '1.2'
              }}>
                HEALTH CANADA LICENSED
              </div>
              <div style={{ 
                fontSize: '11px', 
                color: '#2563eb', 
                fontWeight: '500',
                lineHeight: '1.2'
              }}>
                MANUFACTURER
              </div>
            </div>
          </div>
        </div>

        {/* System Title - Hidden on Mobile */}
        <div style={{ 
          textAlign: 'center', 
          flex: 1, 
          margin: '0 32px'
        }} className="hidden md:block">
          <div style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
            Naloxone Kit Manufacturing System
          </div>
          <div style={{ fontSize: '14px', color: '#2563eb' }}>
            Regulatory Compliance & Traceability Platform
          </div>
        </div>

        {/* Search Bar - Desktop Only */}
        <div style={{ 
          flex: 1, 
          maxWidth: '384px', 
          margin: '0 16px'
        }} className="hidden lg:block">
          <div style={{ position: 'relative', width: '100%' }}>
            <Search style={{ 
              position: 'absolute', 
              left: '12px', 
              top: '50%', 
              transform: 'translateY(-50%)', 
              color: '#9ca3af', 
              width: '20px', 
              height: '20px' 
            }} />
            <input
              type="text"
              placeholder="Search batches, components, records..."
              style={{
                width: '100%',
                paddingLeft: '40px',
                paddingRight: '16px',
                paddingTop: '10px',
                paddingBottom: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: '#f9fafb',
                outline: 'none'
              }}
            />
          </div>
        </div>

        {/* Right Menu */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Notifications */}
          <div style={{ position: 'relative' }} data-dropdown>
            <button 
              onClick={() => setShowNotifications(!showNotifications)}
              style={{
                position: 'relative',
                padding: '10px',
                color: '#6b7280',
                backgroundColor: showNotifications ? '#eff6ff' : '#f9fafb',
                border: showNotifications ? '1px solid #2563eb' : '1px solid #d1d5db',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              <Bell style={{ width: '20px', height: '20px' }} />
              <span style={{
                position: 'absolute',
                top: '-2px',
                right: '-2px',
                backgroundColor: '#ef4444',
                color: 'white',
                fontSize: '12px',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                3
              </span>
            </button>
            
            {showNotifications && (
              <div style={{
                position: 'absolute',
                top: '110%',
                right: '0',
                backgroundColor: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                width: '320px',
                zIndex: 1100,
                maxHeight: '400px',
                overflowY: 'auto'
              }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '14px', color: '#1f2937' }}>Notifications</div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>You have 3 new alerts</div>
                  </div>
                  <button 
                    onClick={(e) => { e.stopPropagation(); setShowNotifications(false); }}
                    style={{ padding: '4px', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '20px' }}
                    title="Close"
                  >
                    ×
                  </button>
                </div>
                
                <div style={{ padding: '8px 0' }}>
                  <div 
                    onClick={() => { alert('Low Stock Alert\n\nNaloxone Spray below minimum threshold\n\nClick to view inventory details.'); closeAllDropdowns(); }}
                    style={{ padding: '12px 16px', borderLeft: '3px solid #ef4444', backgroundColor: '#fef2f2', cursor: 'pointer' }}
                  >
                    <div style={{ fontSize: '13px', fontWeight: '500', color: '#1f2937', marginBottom: '4px' }}>Low Stock Alert</div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Naloxone Spray below minimum threshold</div>
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>2 hours ago</div>
                  </div>
                  
                  <div 
                    onClick={() => { alert('Batch Completed\n\nBatch NAL-2024-001 ready for QC\n\nClick to view batch details.'); closeAllDropdowns(); }}
                    style={{ padding: '12px 16px', borderLeft: '3px solid #10b981', backgroundColor: '#f0fdf4', cursor: 'pointer' }}
                  >
                    <div style={{ fontSize: '13px', fontWeight: '500', color: '#1f2937', marginBottom: '4px' }}>Batch Completed</div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Batch NAL-2024-001 ready for QC</div>
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>4 hours ago</div>
                  </div>
                  
                  <div 
                    onClick={() => { alert('Quality Check Required\n\n3 batches pending inspection\n\nClick to view QC queue.'); closeAllDropdowns(); }}
                    style={{ padding: '12px 16px', borderLeft: '3px solid #f59e0b', backgroundColor: '#fffbeb', cursor: 'pointer' }}
                  >
                    <div style={{ fontSize: '13px', fontWeight: '500', color: '#1f2937', marginBottom: '4px' }}>Quality Check Required</div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>3 batches pending inspection</div>
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>Yesterday</div>
                  </div>
                </div>
                
                <div style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb', textAlign: 'center' }}>
                  <button 
                    onClick={() => { alert('All Notifications\n\nThis would show a full notifications page.'); closeAllDropdowns(); }}
                    style={{ color: '#2563eb', fontSize: '13px', fontWeight: '500', background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    View All Notifications
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Settings */}
          <div style={{ position: 'relative' }} data-dropdown>
            <button 
              onClick={() => setShowSettings(!showSettings)}
              style={{
                padding: '10px',
                color: '#6b7280',
                backgroundColor: showSettings ? '#eff6ff' : '#f9fafb',
                border: showSettings ? '1px solid #2563eb' : '1px solid #d1d5db',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              <Settings style={{ width: '20px', height: '20px' }} />
            </button>
            
            {showSettings && (
              <div style={{
                position: 'absolute',
                top: '110%',
                right: '0',
                backgroundColor: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                width: '240px',
                zIndex: 1100
              }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontWeight: '600', fontSize: '14px', color: '#1f2937' }}>System Settings</div>
                  <button 
                    onClick={(e) => { e.stopPropagation(); setShowSettings(false); }}
                    style={{ padding: '4px', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '20px' }}
                    title="Close"
                  >
                    ×
                  </button>
                </div>
                
                <div style={{ padding: '4px 0' }}>
                  <button 
                    onClick={() => { alert('User Preferences\n\nConfigure your personal settings, language, and display options.'); closeAllDropdowns(); }}
                    style={{ width: '100%', textAlign: 'left', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    User Preferences
                  </button>
                  
                  <button 
                    onClick={() => { alert('System Configuration\n\nManage system-wide settings and parameters.'); closeAllDropdowns(); }}
                    style={{ width: '100%', textAlign: 'left', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    System Configuration
                  </button>
                  
                  <button 
                    onClick={() => { alert('Backup Settings\n\nConfigure automatic backups and data retention.'); closeAllDropdowns(); }}
                    style={{ width: '100%', textAlign: 'left', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    Backup Settings
                  </button>
                  
                  <button 
                    onClick={() => { alert('Security Settings\n\nManage passwords, two-factor authentication, and access controls.'); closeAllDropdowns(); }}
                    style={{ width: '100%', textAlign: 'left', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    Security Settings
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* User Profile */}
          <div style={{ position: 'relative' }} data-dropdown>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                backgroundColor: showUserMenu ? '#dbeafe' : '#eff6ff',
                borderRadius: '6px',
                padding: '8px 12px',
                border: showUserMenu ? '1px solid #2563eb' : '1px solid #bfdbfe',
                cursor: 'pointer'
              }}
            >
              <div style={{ textAlign: 'right' }} className="hidden sm:block">
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                  {currentUser?.name || 'Unknown User'}
                </div>
                <div style={{ fontSize: '12px', color: '#2563eb' }}>
                  {currentUser?.role || 'No Role'}
                </div>
              </div>
              <div style={{
                width: '32px',
                height: '32px',
                backgroundColor: '#dbeafe',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid #93c5fd'
              }}>
                <User style={{ width: '16px', height: '16px', color: '#1d4ed8' }} />
              </div>
            </button>
            
            {showUserMenu && (
              <div className="dropdown-menu-mobile" style={{
                position: 'absolute',
                top: '110%',
                right: '0',
                backgroundColor: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                width: '280px',
                maxWidth: '90vw',
                zIndex: 1100
              }}>
                <div style={{ padding: '16px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '14px', color: '#1f2937' }}>
                      {currentUser?.name || 'Unknown User'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#2563eb', marginTop: '2px' }}>
                      {currentUser?.role || 'No Role'}
                    </div>
                    {currentUser?.permissions && (
                      <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '8px' }}>
                        Permissions: {currentUser.permissions.join(', ')}
                      </div>
                    )}
                  </div>
                  <button 
                    onClick={(e) => { e.stopPropagation(); setShowUserMenu(false); }}
                    style={{ padding: '4px', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '20px' }}
                    title="Close"
                  >
                    ×
                  </button>
                </div>
                
                <div style={{ padding: '4px 0' }}>
                  <button 
                    onClick={() => { alert('Update Profile\n\nEdit your profile information, contact details, and preferences.'); closeAllDropdowns(); }}
                    style={{ width: '100%', textAlign: 'left', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    Update Profile
                  </button>
                  
                  <button 
                    onClick={() => { alert('Change Password\n\nUpdate your account password for security.'); closeAllDropdowns(); }}
                    style={{ width: '100%', textAlign: 'left', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    Change Password
                  </button>
                  
                  <button 
                    onClick={() => { alert('View Activity Log\n\nSee your recent activity and system access history.'); closeAllDropdowns(); }}
                    style={{ width: '100%', textAlign: 'left', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    View Activity Log
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Logout */}
          <button 
            onClick={onLogout}
            style={{
              padding: '10px',
              color: '#6b7280',
              backgroundColor: '#f9fafb',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
            title="Sign Out"
          >
            <LogOut style={{ width: '20px', height: '20px' }} />
          </button>
        </div>
      </div>


    </div>
  );
};

export default WMSHeader;